import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "../context/AuthContext";
import CompanyCard from "../components/company/CompanyCard";

/* ===================================================== */
/* SELECT COMPANY PAGE                                   */
/* ===================================================== */

export default function SelectCompanyPage() {
  const navigate = useNavigate();

  const {
    companies,
    companyId,
    selectCompany,
    loading,
    refreshCompanies,
  } = useAuth();

  const [error, setError] = useState("");
  const [selectingId, setSelectingId] = useState(null);

  /* ===================================================== */
  /* LOAD COMPANIES                                        */
  /* ===================================================== */

  useEffect(() => {
    const load = async () => {
      if (!companies || companies.length === 0) {
        try {
          await refreshCompanies();
        } catch (e) {
          setError("Failed to load companies");
        }
      }
    };

    load();
  }, [companies, refreshCompanies]);

  /* ===================================================== */
  /* REDIRECT                                              */
  /* ===================================================== */

  useEffect(() => {
    if (companyId) {
      navigate("/dashboard", { replace: true });
    }
  }, [companyId, navigate]);

  /* ===================================================== */
  /* HANDLE SELECT                                         */
  /* ===================================================== */

  const handleSelect = async (id) => {
    if (!id) return;

    setSelectingId(id);
    setError("");

    try {
      await selectCompany(id);
    } catch (e) {
      setError(e.message || "Failed to select company");
      setSelectingId(null);
    }
  };

  /* ===================================================== */
  /* UI STATES                                             */
  /* ===================================================== */

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-500">
        Loading companies…
      </div>
    );
  }

  /* ===================================================== */
  /* RENDER                                                */
  /* ===================================================== */

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">

      <div className="w-full max-w-md">

        {/* HEADER */}
        <div className="text-center mb-8">
          <h1 className="text-xl font-semibold text-gray-900">
            Select Workspace
          </h1>
          <p className="text-sm text-gray-500 mt-2">
            Choose a company to continue
          </p>
        </div>

        {/* ERROR */}
        {error && (
          <div className="mb-4 rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700 flex justify-between">
            <span>{error}</span>
            <button
              onClick={refreshCompanies}
              className="underline"
            >
              Retry
            </button>
          </div>
        )}

        {/* EMPTY STATE */}
        {(!companies || companies.length === 0) && (
          <div className="bg-white border rounded-xl p-6 text-center">
            <p className="text-sm text-gray-600 mb-4">
              You are not part of any company yet
            </p>

            <button
              onClick={() => navigate("/create-company")}
              className="px-4 py-2 text-sm bg-orange-500 text-white rounded-lg hover:opacity-90"
            >
              Create Company
            </button>
          </div>
        )}

        {/* COMPANY LIST */}
        {companies && companies.length > 0 && (
          <div className="space-y-3">
            {companies.map((company) => {
              const isSelecting = selectingId === company._id;

              return (
                <button
                  key={company._id}
                  onClick={() => handleSelect(company._id)}
                  disabled={isSelecting}
                  className="w-full text-left disabled:opacity-60"
                >
                  <CompanyCard company={company} />

                  {isSelecting && (
                    <div className="text-xs text-gray-500 mt-1">
                      Switching…
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}

      </div>
    </div>
  );
}